"use client";

import React, { useEffect, useRef, useState } from "react";
import { Shield, Cpu, Network, Send, Loader, CircleCheck, CircleAlert, Server, Lock, Video, Mail, Globe, Bell } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const SERVICE_OPTIONS = [{
  label: "Δίκτυο",
  icon: Network
}, {
  label: "Servers",
  icon: Server
}, {
  label: "Access Controls",
  icon: Lock
}, {
  label: "Κάμερες",
  icon: Video
}, {
  label: "Συστήματα Συναγερμού",
  icon: Bell
}, {
  label: "Emails",
  icon: Mail
}, {
  label: "Website",
  icon: Globe
}];
function SmartOfficeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
                <div className="relative mb-6">
                    <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse" />
                    <CircleCheck className="relative size-20 text-primary" />
                </div>
                <h3 className="mb-2 text-2xl font-bold text-foreground">Αίτημα Ελήφθη Επιτυχώς</h3>
                <div className="mb-8 text-muted-foreground max-w-md">
                    {successMessage}
                </div>
                <Button onClick={resetForm} variant="outline" className="border-primary/50 hover:bg-primary/10 hover:text-primary">
                    Νέα Υποβολή
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
                <FormField control={control} name="name" rules={{
        required: "Το όνομα είναι υποχρεωτικό"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-muted-foreground">Το Όνομά Σας</FormLabel>
                            <FormControl>
                                <Input placeholder="Γιάννης Παπαδόπουλος" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Το email είναι υποχρεωτικό",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Μη έγκυρη διεύθυνση email"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-muted-foreground">Διεύθυνση Email</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="example@domain.com" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="phone" rules={{
      required: "Το τηλέφωνο είναι υποχρεωτικό",
      pattern: {
        value: /^[0-9+\s-]{10,15}$/,
        message: "Μη έγκυρος αριθμός τηλεφώνου"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-muted-foreground">Τηλέφωνο Επικοινωνίας</FormLabel>
                        <FormControl>
                            <Input type="tel" placeholder="+30 210 1234567" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="services_interested" rules={{
      required: "Επιλέξτε τουλάχιστον μία υπηρεσία"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-muted-foreground">Υπηρεσίες που σας ενδιαφέρουν</FormLabel>
                        <FormDescription className="text-xs mb-3">Επιλέξτε τις τεχνολογικές ανάγκες του γραφείου σας.</FormDescription>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                            {SERVICE_OPTIONS.map((option, i) => {
          const isChecked = field.value?.includes(option.label);
          const Icon = option.icon;
          return <label key={option.label} data-index={i} className={cn("flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border p-3 text-center text-sm transition-all duration-200", isChecked ? "border-primary bg-primary/10 text-primary shadow-[0_0_15px_rgba(var(--color-primary),0.15)]" : "border-border/50 bg-background/30 text-muted-foreground hover:border-primary/50 hover:bg-background/50")}>
                                        <input type="checkbox" className="sr-only" checked={isChecked} onChange={e => {
              const current = field.value || [];
              const updated = e.target.checked ? [...current, option.label] : current.filter((val: string) => val !== option.label);
              field.onChange(updated);
            }} />
                                        <Icon className={cn("size-5", isChecked ? "text-primary" : "text-muted-foreground/70")} />
                                        <span className="font-medium leading-tight">{option.label}</span>
                                    </label>;
        })}
                        </div>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-muted-foreground">Πρόσθετες Πληροφορίες / Απαιτήσεις</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Περιγράψτε τυχόν ειδικές ανάγκες ή ερωτήσεις..." className="min-h-[120px] resize-y bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 transition-all" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
                    <CircleAlert className="size-5 shrink-0" />
                    <p className="text-sm">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full group relative overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(var(--color-primary),0.2)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.4)] transition-all duration-300 h-12 text-base font-semibold">
                <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.2)_50%,transparent_75%)] bg-[length:250%_250%,100%_100%] bg-[position:200%_0,0_0] bg-no-repeat transition-[background-position_0s_ease] group-hover:bg-[position:-100%_0,0_0] group-hover:duration-[1500ms]" />
                {isSubmitting ? <>
                        <Loader className="mr-2 size-5 animate-spin" />
                        Αποστολή Δεδομένων...
                    </> : <>
                        <Send className="mr-2 size-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                        Αποστολή Αιτήματος
                    </>}
            </Button>
        </Form>;
}
export default function DesignYourSmartOffice() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  const benefits = ["Άμεση ανταπόκριση σε επείγοντα περιστατικά", "Εξατομικευμένες υπηρεσίες για μικρές επιχειρήσεις", "Ολοκληρωμένες λύσεις πληροφορικής"];
  return <section id="σχεδιάστε-το-έξυπνο-γραφείο-σας" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32" ref={sectionRef}>
            
            <div className="absolute inset-0 pointer-events-none">
                
                <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} />
                
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] -translate-y-1/2" />
                <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-secondary/10 rounded-full blur-[150px] translate-y-1/3" />
            </div>

            <div className="container relative z-10 mx-auto px-4 md:px-6">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-16 items-start">
                    
                    
                    <div className={cn("flex flex-col justify-center space-y-8 transition-all duration-1000", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12")}>
                        <div className="space-y-4">
                            <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary backdrop-blur-sm">
                                <Cpu className="mr-2 size-4" />
                                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/logo_ds_services.png" className="h-7" alt="Ds-Services" />
                            </div>
                            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:leading-[1.1]">Φόρμα&nbsp;<span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Επικοινωνίας</span></h2>
                            <p className="max-w-[600px] text-lg text-muted-foreground leading-relaxed">
                                Αναβαθμίστε την επιχείρησή σας με σύγχρονες, ασφαλείς και αξιόπιστες λύσεις πληροφορικής. Συμπληρώστε τη φόρμα για να ζητήσετε δωρεάν μελέτη του χώρου σας ή άμεση τεχνική υποστήριξη.
                            </p>
                        </div>

                        <div className="space-y-5">
                            {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                                    <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary shadow-[0_0_10px_rgba(var(--color-primary),0.2)]">
                                        <Shield className="size-4" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-foreground">{benefit}</p>
                                    </div>
                                </div>)}
                        </div>

                        <div className="rounded-xl border border-border/50 bg-card/30 p-6 backdrop-blur-sm">
                            <h4 className="mb-2 font-semibold text-foreground">Χρειάζεστε άμεση βοήθεια;</h4>
                            <p className="text-sm text-muted-foreground mb-4">
                                Η ομάδα μας είναι διαθέσιμη Δευτέρα-Παρασκευή: 08:00-18:00.
                            </p>
                            <div className="flex items-center gap-3 text-primary font-mono">
                                <div className="size-2 rounded-full bg-primary animate-pulse" />
                                +30 215 215 8367
                            </div>
                        </div>
                    </div>

                    
                    <div className={cn("relative transition-all duration-1000 delay-200", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
                        
                        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-primary/30 to-transparent opacity-50" />
                        
                        <div className="relative rounded-2xl border border-border/50 bg-card/40 p-6 sm:p-8 backdrop-blur-xl shadow-2xl shadow-black/50">
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-foreground">Φόρμα Επικοινωνίας</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Τα δεδομένα σας μεταφέρονται με ασφάλεια.
                                </p>
                            </div>

                            <FormProvider formId="smart_office_design_contact" sectionName="DesignYourSmartOffice" defaultValues={{
              name: "",
              email: "",
              phone: "",
              services_interested: [],
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              services_interested: {
                required: true
              }
            }} submitText="Αποστολή Αιτήματος" successMessage={<span>Ευχαριστούμε για το ενδιαφέρον σας! Ένας εξειδικευμένος τεχνικός της <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/logo_ds_services.png" className="h-7 inline-block mx-1 align-middle" alt="Ds-Services" /> θα επικοινωνήσει μαζί σας σύντομα.</span>} errorMessage="Παρουσιάστηκε σφάλμα κατά την αποστολή. Παρακαλώ δοκιμάστε ξανά ή καλέστε μας.">
                                <SmartOfficeForm />
                            </FormProvider>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}